import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, Send } from 'lucide-react';
import { colors, companyInfo, servicesList } from '../data';

const Podcast = () => {
  const [status, setStatus] = useState(null); // 'loading', 'success', 'error'

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');

    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setStatus('success');
        e.target.reset();
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  return (
    <div className="overflow-hidden">
      <Helmet>
        <title>Podcast | Pia Meyer - Meyer Management ApS</title>
        <meta name="description" content="Hør min podcast om økonomiledelse, trivsel og processer i økonomifunktionen." />
      </Helmet>

      {/* Header Section */}
      <section className={`pt-32 pb-16 ${colors.bg}`}>
        <div className="container mx-auto px-6 max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className={`text-4xl md:text-6xl font-serif font-bold ${colors.contrast} mb-6`}>
              Podcast: Tal & Trivsel
            </h1>
            <p className="text-xl text-stone-600 mb-10 max-w-2xl mx-auto leading-relaxed">
              En podcast om at skabe balance i økonomifunktionen, hvor vi dykker ned i både de hårde tal og de bløde værdier.
            </p>
            <a 
              href="#podcast-contact"
              className={`inline-block px-10 py-4 ${colors.contrastBg} text-white font-bold rounded-full hover:opacity-90 transition-all shadow-lg shadow-stone-200`}
            >
              Kontakt mig
            </a>
          </motion.div>
        </div>
      </section>

      {/* Hero / Intro Section (Zigzag 1: Image Left, Text Right) */}
      <section className="relative bg-white">
        <div className="flex flex-col md:flex-row min-h-[600px]">
          <div className="w-full md:w-1/2 relative h-[400px] md:h-auto">
            <img
              src="https://assets.auxodev.dk/assets/b8a7f302-f5bc-457e-a48c-e9d29dfda424/ca252a42-8dae-4eff-aea4-f88694b3962f.jpg"
              alt="Podcast optagelse"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
          <div className="w-full md:w-1/2 flex items-center justify-center p-8 md:p-16 lg:p-24">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="max-w-xl"
            >
              <h2 className={`text-4xl md:text-5xl font-serif font-bold ${colors.contrast} mb-8`}>
                Podcast om økonomi og mennesker
              </h2>
              <div className="prose prose-lg text-stone-600 space-y-6">
                <p>
                  Jeg er i gang med at udvikle en podcast, der dykker ned i spændingsfeltet mellem tal og trivsel. Her vil jeg dele ud af mine erfaringer fra økonomifunktionen og invitere spændende gæster til en snak om ledelse, processer og hvordan vi skaber en sund arbejdskultur.
                </p>
                <p>
                  Formålet er at skabe et rum for inspiration og refleksion for alle, der arbejder med økonomi, ledelse eller organisationsudvikling. Vi skal tale om de udfordringer, vi møder i hverdagen, og finde konkrete løsninger, der gør en forskel.
                </p>
                <p className={`font-bold ${colors.contrast}`}>
                  Hold øje med siden – de første episoder er snart på vej.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Section (Zigzag 2: Text/Form Left, Image Right) */}
      <section id="podcast-contact" className={`relative ${colors.bg}`}>
        <div className="flex flex-col-reverse md:flex-row min-h-[700px]">
          <div className="w-full md:w-1/2 flex items-center justify-center p-8 md:p-16 lg:p-24">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="w-full max-w-xl"
            >
              <h2 className={`text-3xl md:text-4xl font-serif font-bold ${colors.contrast} mb-8`}>
                Vil du være gæst eller har du et emne?
              </h2>
              <p className="text-stone-600 mb-10 leading-relaxed">
                Har du en god historie, en særlig ekspertise eller et emne, du synes vi skal tage op i podcasten? Så tøv ikke med at række ud. Jeg er altid på udkig efter spændende vinkler og gæster.
              </p>

              {status === 'success' ? (
                <div className="text-center py-12">
                  <CheckCircle size={64} className="text-green-500 mx-auto mb-6" />
                  <h2 className={`text-2xl font-serif font-bold ${colors.contrast} mb-4`}>Tak for din besked!</h2>
                  <p className="text-stone-600 mb-8">Jeg vender tilbage til dig så hurtigt som muligt.</p>
                  <button 
                    onClick={() => setStatus(null)}
                    className={`px-8 py-3 rounded-full border border-stone-200 text-stone-600 hover:bg-stone-50 transition-all`}
                  >
                    Send en ny besked
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Honeypot */}
                  <div className="sr-only absolute opacity-0 -z-10">
                    <label htmlFor="website_url">Website URL</label>
                    <input type="text" id="website_url" name="website_url" tabIndex="-1" autoComplete="off" />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-bold text-stone-700 mb-2">Navn</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        placeholder="Dit fulde navn"
                        className="w-full px-4 py-3 bg-white border border-stone-200 focus:border-stone-400 outline-none transition-all rounded-none"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-bold text-stone-700 mb-2">Email</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        placeholder="din@mail.dk"
                        className="w-full px-4 py-3 bg-white border border-stone-200 focus:border-stone-400 outline-none transition-all rounded-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-bold text-stone-700 mb-2">Telefonnummer</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      placeholder="Dit telefonnummer"
                      className="w-full px-4 py-3 bg-white border border-stone-200 focus:border-stone-400 outline-none transition-all rounded-none"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-bold text-stone-700 mb-2">Emne</label>
                    <select 
                      id="subject" 
                      name="subject" 
                      className="w-full px-4 py-3 bg-white border border-stone-200 focus:border-stone-400 outline-none transition-all rounded-none"
                    >
                      <option>Podcast gæst / Emneforslag</option>
                      {servicesList.map((service) => (
                        <option key={service.name}>{service.name}</option>
                      ))}
                      <option>Andet</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-bold text-stone-700 mb-2">Besked</label>
                    <textarea
                      id="message"
                      name="message"
                      rows="4"
                      required
                      placeholder="Hvordan kan jeg hjælpe dig?"
                      className="w-full px-4 py-3 bg-white border border-stone-200 focus:border-stone-400 outline-none transition-all resize-none rounded-none"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className={`w-full md:w-auto px-12 py-4 ${colors.contrastBg} text-white font-bold rounded-full hover:opacity-90 transition-all shadow-lg shadow-stone-200 flex items-center justify-center space-x-2 disabled:opacity-50`}
                  >
                    {status === 'loading' ? (
                      <span>Sender...</span>
                    ) : (
                      <>
                        <span>Send besked</span>
                        <Send size={18} />
                      </>
                    )}
                  </button>

                  {status === 'error' && (
                    <p className="text-red-500 text-sm">Der opstod en fejl. Prøv venligst igen eller ring direkte.</p>
                  )}
                </form>
              )}
            </motion.div>
          </div>
          <div className="w-full md:w-1/2 relative h-[400px] md:h-auto">
            <img
              src="https://assets.auxodev.dk/assets/b8a7f302-f5bc-457e-a48c-e9d29dfda424/29f3fda7-806c-4258-82d0-8c9c22f77bd6.jpg"
              alt="Kontakt mig om podcast"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Podcast;
